import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Home, FileText, ChevronRight, Loader2, Truck, Plane, PlaneTakeoff, Ship, Train, Package, Save, X, Printer, FolderOpen, MapPin, ClipboardList, BadgeCheck, PackageCheck, LayoutList, ArrowUpFromLine, Anchor, Receipt, Award, FileDown, FileOutput, ScrollText, ListChecks, Layers, RefreshCw, FolderUp, Tag, CheckSquare } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AirJobSheetForm from './schemas/AirJobSheetForm';
import RoadRailV2Form from './schemas/RoadRailV2Form';
import CourierDocketForm from './schemas/CourierDocketForm';
import InventorySelector from '../components/InventorySelector';
import CreateLabelModal from '../components/CreateLabelModal';
import AiSmartIngestModal from '../components/AiSmartIngestModal';
import { Sparkles } from 'lucide-react';

// Shortcode Icon Buttons Component (Header Quick Actions)
const ShortcodeButtons = ({ docketId, docketType, shipmentType, isSeaDocket, businessModes = [], isConsolidation = false, hasHouseAwb = false, onActionClick }) => {
  const navigate = useNavigate();
  const isFreightForwarding = businessModes.includes('FREIGHT_FORWARDING');
  
  // Common shortcode buttons
  const commonShortcuts = [
    { id: 'print',    icon: Printer,      color: 'bg-orange-500 hover:bg-orange-600',   title: 'Print Docket',      onClick: () => navigate(`/dockets/print/${docketId}`) },
    { id: 'label',    icon: Tag,          color: 'bg-emerald-600 hover:bg-emerald-700', title: 'Print Label',       onClick: () => onActionClick ? onActionClick('print_label') : navigate(`/dockets/print-label/${docketId}`) },
    { id: 'store',    icon: FolderOpen,   color: 'bg-pink-500 hover:bg-pink-600',        title: 'Store Documents',   onClick: () => navigate(`/dockets/${docketId}/store-documents`) },
    { id: 'tracking', icon: MapPin,       color: 'bg-slate-700 hover:bg-slate-800',      title: 'Update Tracking',   onClick: () => navigate(`/dockets/${docketId}/tracking`) },
  ];

  const canRoute = shipmentType === 'SEA' ? `/dockets/${docketId}/sea-cargo-arrival-notice` : `/dockets/${docketId}/cargo-arrival-notice`;
  const doRoute  = shipmentType === 'SEA' ? `/dockets/${docketId}/sea-delivery-order`       : `/dockets/${docketId}/delivery-order`;
  const fcRoute  = shipmentType === 'SEA' ? `/dockets/${docketId}/sea-freight-certificate`  : `/dockets/${docketId}/freight-certificate`;

  const inwardShortcuts = [
    { id: 'cargo_notice',   icon: PackageCheck,  color: 'bg-cyan-500 hover:bg-cyan-600',   title: 'Cargo Arrival Notice', onClick: () => navigate(canRoute) },
    { id: 'delivery_order', icon: ClipboardList, color: 'bg-green-500 hover:bg-green-600', title: 'Delivery Order',       onClick: () => navigate(doRoute) },
    { id: 'freight_cert',   icon: BadgeCheck,    color: 'bg-red-500 hover:bg-red-600',     title: 'Freight Certificate',  onClick: () => navigate(fcRoute) },
  ];

  // Outward shortcuts — same colors & logic as bottom buttons; House AWB shown when consolidation=Yes
  const outwardShortcuts = [
    { id: 'air_waybill',  icon: Plane,       color: 'bg-violet-600 hover:bg-violet-700',  title: 'Master Airway Bill', onClick: () => navigate(`/dockets/${docketId}/air-waybill`) },
    ...(hasHouseAwb ? [{ id: 'house_air_waybill', icon: PlaneTakeoff, color: 'bg-fuchsia-600 hover:bg-fuchsia-700', title: 'House Airway Bill', onClick: () => navigate(`/dockets/${docketId}/house-air-waybill`) }] : []),
    ...(isConsolidation ? [{ id: 'air_manifests',  icon: LayoutList,  color: 'bg-purple-600 hover:bg-purple-700',  title: 'Air Manifests',      onClick: () => navigate(`/dockets/${docketId}/air-manifests`) }] : []),
    { id: 'lifting_advice', icon: ArrowUpFromLine, color: 'bg-rose-600 hover:bg-rose-700', title: 'Lifting Advice',     onClick: () => navigate(`/dockets/${docketId}/lifting-advice`) },
  ];

  // SEA Outward shortcuts
  const seaOutwardShortcuts = [
    { id: 'sea_mt_documents', icon: Ship, color: 'bg-teal-500 hover:bg-teal-600', title: 'BL / MT Document', onClick: () => navigate(`/dockets/${docketId}/sea-mt-documents`) },
    ...(isConsolidation ? [
      { id: 'master_bl', icon: Anchor,  color: 'bg-violet-600 hover:bg-violet-700',  title: 'Master Bill of Lading', onClick: () => navigate(`/dockets/${docketId}/air-waybill`) },
      { id: 'house_bl',  icon: Anchor,  color: 'bg-fuchsia-600 hover:bg-fuchsia-700', title: 'House Bill of Lading',  onClick: () => navigate(`/dockets/${docketId}/house-air-waybill`) },
    ] : []),
  ];

  // ROAD/RAIL Outward shortcuts
  const isRoadRailShortcut = shipmentType === 'ROAD' || shipmentType === 'RAIL';
  const roadRailOutwardShortcuts = [
    { id: 'lr_receipts', icon: Receipt, color: 'bg-orange-500 hover:bg-orange-600', title: 'LR Receipts', onClick: () => navigate(`/dockets/${docketId}/lr-receipts`) },
  ];

  const isCourierShortcut = shipmentType === 'COURIER';
  const typeShortcuts = isCourierShortcut
    ? []
    : isFreightForwarding
      ? (docketType === 'Inward' ? inwardShortcuts
        : docketType === 'Outward'
          ? (isRoadRailShortcut ? roadRailOutwardShortcuts : isSeaDocket ? seaOutwardShortcuts : outwardShortcuts)
          : [])
      : (docketType === 'Outward' && isRoadRailShortcut ? roadRailOutwardShortcuts : []);

  const allShortcuts = isCourierShortcut
    ? [commonShortcuts[0], commonShortcuts[1]]
    : [...commonShortcuts, ...typeShortcuts];

  return (
    <div className="flex items-center gap-1">
      {allShortcuts.map((shortcut) => (
        <button
          key={shortcut.id}
          data-testid={`shortcut-${shortcut.id}`}
          onClick={shortcut.onClick}
          title={shortcut.title}
          className={`${shortcut.color} text-white w-10 h-10 rounded-lg flex items-center justify-center transition-all hover:shadow-md active:scale-95`}
        >
          <shortcut.icon className="w-5 h-5" />
        </button>
      ))}
    </div>
  );
};

// Document Actions Section Component
const DocumentActionsSection = ({ docketId, docketType, systemDocketNo, shipmentType, isSeaDocket, businessModes = [], isConsolidation = false, hasHouseAwb = false, onActionClick }) => {
  const navigate = useNavigate();
  const isFreightForwarding = businessModes.includes('FREIGHT_FORWARDING');
  
  // Common buttons for both Inward and Outward
  const commonActions = [
    { 
      id: 'print_docket', 
      label: 'Print Docket', 
      icon: Printer,
      color: 'bg-orange-500 hover:bg-orange-600',
      onClick: () => navigate(`/dockets/print/${docketId}`)
    },
    { 
      id: 'print_label', 
      label: 'Print Label', 
      icon: Tag,
      color: 'bg-emerald-600 hover:bg-emerald-700',
      onClick: () => onActionClick ? onActionClick('print_label') : navigate(`/dockets/print-label/${docketId}`)
    },
    { 
      id: 'store_documents', 
      label: 'Store Documents', 
      icon: FolderOpen,
      color: 'bg-pink-500 hover:bg-pink-600',
      onClick: () => navigate(`/dockets/${docketId}/store-documents`)
    },
    { 
      id: 'update_tracking', 
      label: 'Update Tracking Status', 
      icon: MapPin,
      color: 'bg-slate-700 hover:bg-slate-800',
      onClick: () => navigate(`/dockets/${docketId}/tracking`)
    },
  ];

  // Inward AIR actions
  const inwardAirActions = [
    { 
      id: 'cargo_arrival_notice', 
      label: 'Cargo Arrival Notice', 
      icon: PackageCheck,
      color: 'bg-cyan-500 hover:bg-cyan-600',
      onClick: () => navigate(`/dockets/${docketId}/cargo-arrival-notice`)
    },
    { 
      id: 'delivery_order', 
      label: 'Delivery Order', 
      icon: ClipboardList,
      color: 'bg-green-500 hover:bg-green-600',
      onClick: () => navigate(`/dockets/${docketId}/delivery-order`)
    },
    { 
      id: 'freight_certificate', 
      label: 'Freight Certificate', 
      icon: BadgeCheck,
      color: 'bg-red-500 hover:bg-red-600',
      onClick: () => navigate(`/dockets/${docketId}/freight-certificate`)
    },
  ];

  // Inward SEA actions
  const inwardSeaActions = [
    {
      id: 'sea_cargo_arrival_notice',
      label: 'Cargo Arrival Notice',
      icon: PackageCheck,
      color: 'bg-cyan-500 hover:bg-cyan-600',
      onClick: () => navigate(`/dockets/${docketId}/sea-cargo-arrival-notice`)
    },
    { 
      id: 'sea_delivery_order', 
      label: 'Delivery Order', 
      icon: ClipboardList,
      color: 'bg-green-500 hover:bg-green-600',
      onClick: () => navigate(`/dockets/${docketId}/sea-delivery-order`)
    },
    { 
      id: 'freight_certificate', 
      label: 'Freight Certificate', 
      icon: BadgeCheck,
      color: 'bg-red-500 hover:bg-red-600',
      onClick: () => navigate(`/dockets/${docketId}/sea-freight-certificate`)
    },
  ];

  // Outward AIR actions — show House AWB for Back-to-Back and Consolidation
  const outwardActions = [
    { 
      id: 'air_waybill', 
      label: 'Master Airway Bill', 
      icon: Plane,
      color: 'bg-violet-600 hover:bg-violet-700',
      onClick: () => navigate(`/dockets/${docketId}/air-waybill`)
    },
    ...(hasHouseAwb ? [{
      id: 'house_air_waybill',
      label: 'House Airway Bill',
      icon: PlaneTakeoff,
      color: 'bg-fuchsia-600 hover:bg-fuchsia-700',
      onClick: () => navigate(`/dockets/${docketId}/house-air-waybill`)
    }] : []),
    ...(isConsolidation ? [{ 
      id: 'air_manifests', 
      label: 'Air Manifests', 
      icon: LayoutList,
      color: 'bg-purple-600 hover:bg-purple-700',
      onClick: () => navigate(`/dockets/${docketId}/air-manifests`)
    }] : []),
    { 
      id: 'lifting_advice', 
      label: 'Lifting Advice', 
      icon: ArrowUpFromLine,
      color: 'bg-rose-600 hover:bg-rose-700',
      onClick: () => navigate(`/dockets/${docketId}/lifting-advice`)
    },
  ];

  // SEA Outward-specific buttons — add House BL when consolidation
  const outwardSeaActions = [
    {
      id: 'sea_mt_documents',
      label: 'BL / MT Document',
      icon: Ship,
      color: 'bg-teal-500 hover:bg-teal-600',
      onClick: () => navigate(`/dockets/${docketId}/sea-mt-documents`)
    },
    ...(isConsolidation ? [
      {
        id: 'master_bl',
        label: 'Master Bill of Lading',
        icon: Anchor,
        color: 'bg-violet-600 hover:bg-violet-700',
        onClick: () => navigate(`/dockets/${docketId}/air-waybill`)
      },
      {
        id: 'house_bl',
        label: 'House Bill of Lading',
        icon: Anchor,
        color: 'bg-fuchsia-600 hover:bg-fuchsia-700',
        onClick: () => navigate(`/dockets/${docketId}/house-air-waybill`)
      }
    ] : []),
  ];

  // ROAD/RAIL Outward-specific buttons
  const isRoadRailDocket = shipmentType === 'ROAD' || shipmentType === 'RAIL';
  const outwardRoadRailActions = [
    {
      id: 'lr_receipts',
      label: 'LR Receipts',
      icon: Receipt,
      color: 'bg-orange-500 hover:bg-orange-600',
      onClick: () => navigate(`/dockets/${docketId}/lr-receipts`)
    },
  ];

  // Determine which type-specific actions to show
  const isCourier = shipmentType === 'COURIER';
  const typeSpecificActions = isCourier
    ? []
    : docketType === 'Inward'
      ? (isSeaDocket ? inwardSeaActions : inwardAirActions)
      : docketType === 'Outward'
      ? (isRoadRailDocket ? outwardRoadRailActions : isSeaDocket ? [...outwardSeaActions] : outwardActions)
      : [];

  return (
    <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-white" />
            <h2 className="text-base font-semibold text-white font-ibm">Document Actions</h2>
          </div>
          {docketType && (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              docketType === 'Inward' ? 'bg-emerald-500 text-white' : 'bg-violet-500 text-white'
            }`}>
              {docketType} Docket
            </span>
          )}
        </div>
        {systemDocketNo && (
          <p className="text-xs text-gray-300 mt-1 font-mono">{systemDocketNo}</p>
        )}
      </div>

      <div className="p-5">
        {/* Common Actions */}
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3 font-ibm">
            Common Actions
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {commonActions.map((action) => (
              <button
                key={action.id}
                data-testid={`doc-action-${action.id}`}
                onClick={action.onClick}
                className={`${action.color} text-white rounded-lg p-3 flex flex-col items-center justify-center gap-2 transition-all hover:shadow-md active:scale-95`}
              >
                <action.icon className="w-5 h-5" />
                <span className="text-xs font-medium font-ibm text-center">{action.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Type-Specific Actions — only for Freight Forwarding companies */}
        {isFreightForwarding && typeSpecificActions.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3 font-ibm">
              {docketType === 'Inward' ? 'Inward Documents' : 'Outward Documents'}
            </h3>
            <div className={`grid gap-3 ${docketType === 'Outward' ? 'grid-cols-4' : 'grid-cols-3'}`}>
              {typeSpecificActions.map((action) => (
                <button
                  key={action.id}
                  data-testid={`doc-action-${action.id}`}
                  onClick={action.onClick}
                  className={`${action.color} text-white rounded-lg p-3 flex flex-col items-center justify-center gap-2 transition-all hover:shadow-md active:scale-95`}
                >
                  <action.icon className="w-5 h-5" />
                  <span className="text-xs font-medium font-ibm text-center">{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* No docket type selected message — only for Freight Forwarding */}
        {isFreightForwarding && !docketType && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
            <p className="text-xs text-amber-700 font-ibm">
              Please select <strong>Inward</strong> or <strong>Outward</strong> docket type in the form above to see type-specific document actions.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

const CreateDocket = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: docketId } = useParams();
  const { toast } = useToast();
  const isEditMode = !!docketId;
  const isViewMode = location.pathname.includes('/view/');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [allowedShipmentTypes, setAllowedShipmentTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerDetails, setCustomerDetails] = useState({});
  const [locations, setLocations] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [docketData, setDocketData] = useState(null);
  const [resolvedSchema, setResolvedSchema] = useState(null);
  const [pendingFiles, setPendingFiles] = useState({});
  const [storedDocuments, setStoredDocuments] = useState([]);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [showSmartIngest, setShowSmartIngest] = useState(false);

  const handleApplyExtractedData = (extracted) => {
    setFormData(prev => {
      const p = prev.payload_json || {};
      return {
        ...prev,
        payload_json: {
          ...p,
          shipper: {
            ...p.shipper,
            name: extracted.shipper_name || p.shipper?.name,
            address: extracted.shipper_address || p.shipper?.address
          },
          consignee: {
            ...p.consignee,
            name: extracted.consignee_name || p.consignee?.name,
            address: extracted.consignee_address || p.consignee?.address
          },
          routing: {
            ...p.routing,
            origin_city: extracted.origin_city || p.routing?.origin_city,
            destination_city: extracted.destination_city || p.routing?.destination_city
          },
          cargo: {
            ...p.cargo,
            gross_weight: extracted.gross_weight_kg || p.cargo?.gross_weight,
            pieces: extracted.pieces || p.cargo?.pieces,
            commodity: extracted.commodity || p.cargo?.commodity,
            nature_of_goods: extracted.commodity || p.cargo?.nature_of_goods,
            temperature: extracted.temperature_range || p.cargo?.temperature
          }
        }
      };
    });
    toast({
      title: "AI Auto-Fill Applied",
      description: `Extracted ${extracted.commodity || 'shipment data'} and populated form fields!`,
    });
  };

  const [formData, setFormData] = useState({
    company_id: '',
    shipment_type: '',
    printed_docket_no: '',
    payload_json: {},
    inventory_items: [],
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  // Fetch schema mapping
  const fetchResolvedSchema = useCallback(async (companyId, shipmentType) => {
    if (!companyId || !shipmentType) { setResolvedSchema(null); return; }
    try {
      const response = await fetch(`${backendUrl}/api/companies/${companyId}/schema-mappings`, { headers: getAuthHeaders() });
      if (response.ok) {
        const mappings = await response.json();
        const match = mappings.find(m => m.shipment_type === shipmentType);
        setResolvedSchema(match || null);
      }
    } catch (error) { setResolvedSchema(null); }
  }, [backendUrl, getAuthHeaders]);

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/customers?limit=500`, { headers: getAuthHeaders() });
      if (response.ok) { const d = await response.json(); setCustomers(d.items || []); }
    } catch (error) { console.error('Error fetching customers:', error); }
  }, [backendUrl, getAuthHeaders]);

  // Fetch locations
  const fetchLocations = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/locations?is_active=true`, { headers: getAuthHeaders() });
      if (response.ok) setLocations(await response.json());
    } catch (error) { console.error('Error fetching locations:', error); }
  }, [backendUrl, getAuthHeaders]);

  // Fetch carriers
  const fetchCarriers = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/carriers?is_active=true`, { headers: getAuthHeaders() });
      if (response.ok) setCarriers(await response.json());
    } catch (error) { console.error('Error fetching carriers:', error); }
  }, [backendUrl, getAuthHeaders]);

  // Fetch agents
  const fetchAgents = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/agents?is_active=true`, { headers: getAuthHeaders() });
      if (response.ok) setAgents(await response.json());
    } catch (error) { console.error('Error fetching agents:', error); }
  }, [backendUrl, getAuthHeaders]);

  // Fetch companies
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/companies`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
        const userCompanyId = localStorage.getItem('company_id');
        if (userCompanyId && data.find(c => c.id === userCompanyId)) {
          handleCompanyChange(userCompanyId, data);
        }
      }
    } catch (error) { console.error('Error fetching companies:', error); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUrl, getAuthHeaders]);

  // Fetch docket for edit
  const fetchDocket = useCallback(async () => {
    if (!docketId) return;
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/dockets/${docketId}`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to load docket');
      const data = await response.json();
      setDocketData(data);
      setFormData({
        company_id: data.company_id,
        shipment_type: data.shipment_type,
        printed_docket_no: data.printed_docket_no || '',
        payload_json: data.payload_json || {},
        inventory_items: data.inventory_items || [],
      });
      if (data.schema_key) {
        setResolvedSchema({ schema_key: data.schema_key, schema_name: data.schema_key });
      }
      const companyRes = await fetch(`${backendUrl}/api/companies/${data.company_id}`, { headers: getAuthHeaders() });
      if (companyRes.ok) {
        const companyData = await companyRes.json();
        setSelectedCompany(companyData);
        setAllowedShipmentTypes(companyData.allowed_shipment_types || []);
      }

      // Pre-fetch customer details so branch dropdowns populate correctly on load
      const hdr = (data.payload_json || {}).header || {};
      const partyDet = (data.payload_json || {}).party_details || {};
      const customerIds = [
        hdr.bill_to, hdr.exporter_shipper, hdr.importer_receiver, hdr.notify_party,
        partyDet.consignor, partyDet.consignee, partyDet.bill_to,
      ].filter(Boolean);

      if (customerIds.length > 0) {
        const results = await Promise.all(
          customerIds.map(async (cid) => {
            try {
              const res = await fetch(`${backendUrl}/api/customers/${cid}`, { headers: getAuthHeaders() });
              if (res.ok) return { id: cid, data: await res.json() };
            } catch (e) {}
            return null;
          })
        );
        const detailsMap = {};
        results.forEach(r => { if (r) detailsMap[r.id] = r.data; });
        setCustomerDetails(detailsMap);
      }

      // Fetch stored Drive documents
      try {
        const docsRes = await fetch(`${backendUrl}/api/dockets/${docketId}/stored-documents`, { headers: getAuthHeaders() });
        if (docsRes.ok) setStoredDocuments(await docsRes.json());
      } catch (e) {}
    } catch (error) {
      toast({ title: "Error", description: "Failed to load docket data", variant: "destructive" });
      navigate('/dockets');
    } finally { setLoading(false); }
  }, [docketId, backendUrl, getAuthHeaders, toast, navigate]);

  useEffect(() => {
    fetchCompanies();
    fetchCustomers();
    fetchLocations();
    fetchCarriers();
    fetchAgents();
    if (isEditMode) fetchDocket();
  }, [fetchCompanies, fetchCustomers, fetchLocations, fetchCarriers, fetchAgents, fetchDocket, isEditMode]);

  const handleCompanyChange = (companyId, companiesList = companies) => {
    setFormData(prev => ({ ...prev, company_id: companyId, shipment_type: '', payload_json: {} }));
    setResolvedSchema(null);
    const company = companiesList.find(c => c.id === companyId);
    if (company) {
      setSelectedCompany(company);
      setAllowedShipmentTypes(company.allowed_shipment_types || []);
    }
  };

  const handleShipmentTypeChange = (value) => {
    setFormData(prev => ({ ...prev, shipment_type: value, payload_json: {} }));
    if (value) fetchResolvedSchema(formData.company_id, value);
    else setResolvedSchema(null);
  };

  const handlePayloadChange = (newPayload) => {
    setFormData(prev => ({ ...prev, payload_json: newPayload }));
  };

  // Customer detail fetch for auto-populating customer info in schema forms
  const handleCustomerSelectInSchema = useCallback(async (customerId, role) => {
    if (!customerId) return;
    try {
      const response = await fetch(`${backendUrl}/api/customers/${customerId}`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setCustomerDetails(prev => {
          // Only update if not already cached
          if (prev[customerId]) return prev;
          return { ...prev, [customerId]: data };
        });
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
    }
  }, [backendUrl, getAuthHeaders]);

  const handleSave = async () => {
    if (!formData.company_id) { toast({ title: "Error", description: "Please select a company", variant: "destructive" }); return; }
    if (!formData.shipment_type) { toast({ title: "Error", description: "Please select a shipment type", variant: "destructive" }); return; }

    // Note: Required fields validation temporarily relaxed for testing

    try {
      setSaving(true);
      let response;
      if (isEditMode) {
        response = await fetch(`${backendUrl}/api/dockets/${docketId}`, {
          method: 'PUT', headers: getAuthHeaders(),
          body: JSON.stringify({
            printed_docket_no: formData.printed_docket_no || null,
            payload_json: formData.payload_json,
            inventory_item_ids: (formData.inventory_items || []).map(i => i.id),
          })
        });
      } else {
        const requestBody = { 
          company_id: formData.company_id, 
          shipment_type: formData.shipment_type, 
          schema_key: resolvedSchema?.schema_key || null,
          printed_docket_no: formData.printed_docket_no || null, 
          payload_json: formData.payload_json,
          inventory_item_ids: (formData.inventory_items || []).map(i => i.id),
        };
        console.log('Creating docket with data:', JSON.stringify(requestBody, null, 2));
        response = await fetch(`${backendUrl}/api/dockets`, {
          method: 'POST', headers: getAuthHeaders(),
          body: JSON.stringify(requestBody)
        });
      }

      if (!response.ok) {
        const errText = await response.text();
        console.error('Docket API error response:', errText);
        let errDetail = 'Failed to save docket';
        try {
          const errJson = JSON.parse(errText);
          errDetail = errJson.detail || errDetail;
        } catch(e) {}
        throw new Error(errDetail);
      }

      const saved = await response.json();
      const savedId = saved.id || docketId;
      const savedNo = saved.system_docket_no || docketData?.system_docket_no;

      // Upload any pending Drive files (ROAD_RAIL_V2 docket docs)
      const fileEntries = Object.values(pendingFiles);
      if (fileEntries.length > 0 && savedId) {
        const token = localStorage.getItem('auth_token');
        let uploadedCount = 0;
        for (const { file, label } of fileEntries) {
          try {
            const fd = new FormData();
            fd.append('file', file);
            const uploadRes = await fetch(
              `${backendUrl}/api/dockets/${savedId}/stored-documents?document_title=${encodeURIComponent(label)}`,
              { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd }
            );
            if (uploadRes.ok) uploadedCount++;
          } catch (e) { console.error('File upload failed:', e); }
        }
        if (uploadedCount > 0) {
          toast({ title: "Files Uploaded", description: `${uploadedCount} file(s) saved to Google Drive under ${savedNo}` });
        }
      }

      toast({ title: "Success", description: isEditMode ? "Docket updated successfully" : `Docket ${savedNo} created` });

      if (isEditMode) {
        // Stay on the edit page — just refresh docket data so docket number etc. are current
        setDocketData(prev => ({ ...prev, ...saved }));
      } else {
        // After creating, switch to edit mode so user stays in the form
        navigate(`/dockets/edit/${savedId}`, { replace: true });
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const SHIPMENT_ICONS = { ROAD: Truck, AIR: Plane, SEA: Ship, RAIL: Train, COURIER: Package };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8" data-testid="create-docket-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <Home className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/')}>Home</span>
        <ChevronRight className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/dockets')}>Dockets</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-text-dark font-medium">{isEditMode ? 'Edit Docket' : 'Create Docket'}</span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-dark font-manrope">
            {isEditMode ? `Edit Docket — ${docketData?.system_docket_no || ''}` : 'New Docket'}
          </h1>
          <p className="text-sm text-text-secondary font-ibm mt-1">
            {isEditMode ? 'Update docket details' : 'Create a new docket entry'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Shortcode Icon Buttons - Only show in Edit Mode for AIR/SEA/ROAD/RAIL dockets */}
          {isEditMode && ['AIR', 'SEA', 'ROAD', 'RAIL', 'COURIER'].includes(formData.shipment_type) && (
            <ShortcodeButtons 
              docketId={docketId}
              docketType={formData.payload_json?.header?.inward_outward || ''}
              shipmentType={formData.shipment_type || ''}
              isSeaDocket={formData.shipment_type === 'SEA'}
              businessModes={selectedCompany?.business_modes || []}
              isConsolidation={formData.payload_json?.header?.shipment_structure === 'CONSOLIDATION' || formData.payload_json?.header?.consolidation_job === 'Yes'}
              hasHouseAwb={formData.payload_json?.header?.shipment_structure === 'BACK_TO_BACK' || formData.payload_json?.header?.shipment_structure === 'CONSOLIDATION' || formData.payload_json?.header?.consolidation_job === 'Yes' || (!formData.payload_json?.header?.shipment_structure && formData.payload_json?.header?.consolidation_job !== 'Yes' && formData.payload_json?.header?.shipment_structure !== 'DIRECT')}
              onActionClick={(action) => {
                if (action === 'print_label') {
                  setShowLabelModal(true);
                  return;
                }
                toast({
                  title: "Coming Soon",
                  description: `${action} feature will be available soon.`,
                });
              }}
            />
          )}
          <Button
            type="button"
            onClick={() => setShowSmartIngest(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white font-ibm font-medium shadow-sm transition-transform active:scale-95"
            title="Auto-fill form from Invoice or Packing list"
          >
            <Sparkles className="w-4 h-4 mr-2" /> AI Smart Ingest
          </Button>
          <Button variant="outline" onClick={() => navigate('/dockets')} data-testid="cancel-btn">
            <X className="w-4 h-4 mr-2" /> Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-docket-btn" className="bg-accent-blue hover:bg-accent-blue/90">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : isEditMode ? 'Update Docket' : 'Create Docket'}
          </Button>
        </div>
      </div>

      {/* DOCKET INFORMATION — Always shown */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-blue px-6 py-4">
          <h2 className="text-lg font-semibold text-text-dark font-manrope">DOCKET INFORMATION</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Company */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Company <span className="text-red-500">*</span></Label>
              <Select value={formData.company_id || "none"} onValueChange={v => handleCompanyChange(v === "none" ? '' : v)} disabled={isEditMode}>
                <SelectTrigger data-testid="company-select" className="border-gray-200 font-ibm">
                  <SelectValue placeholder="Select Company" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select Company --</SelectItem>
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Shipment Type */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Shipment Type <span className="text-red-500">*</span></Label>
              <Select value={formData.shipment_type || "none"} onValueChange={v => handleShipmentTypeChange(v === "none" ? '' : v)} disabled={isEditMode || !formData.company_id}>
                <SelectTrigger data-testid="shipment-type-select" className="border-gray-200 font-ibm">
                  <SelectValue placeholder="Select Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select Type --</SelectItem>
                  {allowedShipmentTypes.map(type => {
                    const Icon = SHIPMENT_ICONS[type] || Package;
                    return <SelectItem key={type} value={type}><span className="flex items-center gap-2"><Icon className="w-4 h-4" /> {type}</span></SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Printed Docket No */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Printed Docket No</Label>
              <Input data-testid="printed-docket-input" placeholder="Pre-printed book number" value={formData.printed_docket_no} onChange={e => setFormData(prev => ({ ...prev, printed_docket_no: e.target.value }))} className="border-gray-200 font-ibm" />
              <p className="text-xs text-text-secondary">Optional manual docket number</p>
            </div>

            {/* Resolved Schema */}
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Form Schema</Label>
              <div data-testid="resolved-schema-display" className={`flex items-center h-10 px-3 rounded-md border text-sm font-ibm ${resolvedSchema ? 'bg-accent-blue/5 border-accent-blue/30 text-accent-blue' : 'bg-gray-50 border-gray-200 text-text-secondary'}`}>
                {resolvedSchema ? resolvedSchema.schema_name || resolvedSchema.schema_key : formData.shipment_type ? 'No schema mapped' : 'Select shipment type first'}
              </div>
              <p className="text-xs text-text-secondary">Auto-assigned from company settings</p>
            </div>
          </div>
        </div>
      </Card>

      {/* DYNAMIC SCHEMA FORM — Loaded based on resolved schema */}
      {resolvedSchema?.schema_key === 'INTL_AIR_V1' && (
        <AirJobSheetForm
          payload={formData.payload_json}
          onChange={handlePayloadChange}
          customers={customers}
          customerDetails={customerDetails}
          onCustomerSelect={handleCustomerSelectInSchema}
          locations={locations}
          carriers={carriers}
          agents={agents}
          mode="AIR"
          companyId={formData.company_id}
          currentDocketId={isEditMode ? docketId : null}
          shipmentType={formData.shipment_type}
        />
      )}

      {resolvedSchema?.schema_key === 'INTL_SEA_V1' && (
        <AirJobSheetForm
          payload={formData.payload_json}
          onChange={handlePayloadChange}
          customers={customers}
          customerDetails={customerDetails}
          onCustomerSelect={handleCustomerSelectInSchema}
          locations={locations}
          carriers={carriers}
          agents={agents}
          mode="SEA"
          companyId={formData.company_id}
          currentDocketId={isEditMode ? docketId : null}
          shipmentType={formData.shipment_type}
        />
      )}

      {/* Fallback for unmapped or unknown schemas */}
      {formData.shipment_type && !resolvedSchema && (
        <Card className="bg-amber-50 rounded-xl border border-amber-200 shadow-sm">
          <div className="p-6 text-center">
            <p className="text-amber-700 font-ibm">No form schema is mapped to <strong>{formData.shipment_type}</strong> for this company. Please configure it in Company Management → Form Schema Mapping.</p>
          </div>
        </Card>
      )}

      {formData.shipment_type && resolvedSchema && !['INTL_AIR_V1', 'INTL_SEA_V1', 'ROAD_RAIL_V1', 'ROAD_RAIL_V2', 'INTL_COURIER_V1', 'COURIER_V1'].includes(resolvedSchema.schema_key) && (
        <Card className="bg-gray-50 rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6 text-center">
            <p className="text-text-secondary font-ibm">Form for schema <strong>{resolvedSchema.schema_key}</strong> is not yet implemented. Coming soon.</p>
          </div>
        </Card>
      )}

      {/* COURIER form — simple courier form for International and Logistics */}
      {formData.shipment_type && (formData.shipment_type === 'COURIER' || ['INTL_COURIER_V1', 'COURIER_V1'].includes(resolvedSchema?.schema_key)) && (
        <CourierDocketForm
          payload={formData.payload_json}
          onChange={handlePayloadChange}
          customers={customers}
          customerDetails={customerDetails}
          onCustomerSelect={handleCustomerSelectInSchema}
          locations={locations}
          carriers={carriers}
        />
      )}

      {/* ROAD/RAIL V2 form — new logistics-specific layout */}
      {formData.shipment_type && resolvedSchema?.schema_key === 'ROAD_RAIL_V2' && (
        <RoadRailV2Form
          payload={formData.payload_json}
          onChange={handlePayloadChange}
          customers={customers}
          customerDetails={customerDetails}
          onCustomerSelect={handleCustomerSelectInSchema}
          locations={locations}
          onFilesChange={setPendingFiles}
          storedDocuments={storedDocuments}
          inventoryItems={formData.inventory_items}
          onInventoryItemsChange={(items) => setFormData(prev => ({ ...prev, inventory_items: items }))}
          excludeDocketId={isEditMode ? docketId : undefined}
        />
      )}

      {/* ROAD/RAIL V1 form — legacy */}
      {formData.shipment_type && resolvedSchema?.schema_key === 'ROAD_RAIL_V1' && (
        <AirJobSheetForm
          payload={formData.payload_json}
          onChange={handlePayloadChange}
          customers={customers}
          customerDetails={customerDetails}
          onCustomerSelect={handleCustomerSelectInSchema}
          locations={locations}
          carriers={carriers}
          agents={agents}
          mode="ROAD_RAIL"
          schemaKey="ROAD_RAIL_V1"
        />
      )}

      {/* INVENTORY ITEMS — visible once company + shipment_type chosen */}
      {formData.company_id && formData.shipment_type && (
        <InventorySelector
          value={formData.inventory_items}
          onChange={(next) => setFormData(prev => ({ ...prev, inventory_items: next }))}
          excludeDocketId={isEditMode ? docketId : undefined}
        />
      )}

      {/* Document Actions Section - show for AIR, SEA, ROAD, RAIL dockets in edit/view mode */}
      {isEditMode && (
        ['INTL_AIR_V1', 'INTL_SEA_V1'].includes(resolvedSchema?.schema_key) && ['AIR', 'SEA'].includes(formData.shipment_type)
        || ['ROAD_RAIL_V1', 'ROAD_RAIL_V2'].includes(resolvedSchema?.schema_key) && ['ROAD', 'RAIL', 'AIR'].includes(formData.shipment_type)
        || formData.shipment_type === 'COURIER'
      ) && (
        <DocumentActionsSection 
          docketId={docketId}
          docketType={formData.payload_json?.header?.inward_outward || ''}
          shipmentType={formData.shipment_type || ''}
          isSeaDocket={formData.shipment_type === 'SEA'}
          systemDocketNo={docketData?.system_docket_no}
          businessModes={selectedCompany?.business_modes || []}
          isConsolidation={formData.payload_json?.header?.shipment_structure === 'CONSOLIDATION' || formData.payload_json?.header?.consolidation_job === 'Yes'}
              hasHouseAwb={formData.payload_json?.header?.shipment_structure === 'BACK_TO_BACK' || formData.payload_json?.header?.shipment_structure === 'CONSOLIDATION' || formData.payload_json?.header?.consolidation_job === 'Yes' || (!formData.payload_json?.header?.shipment_structure && formData.payload_json?.header?.consolidation_job !== 'Yes' && formData.payload_json?.header?.shipment_structure !== 'DIRECT')}
          onActionClick={(action) => {
            if (action === 'print_label') {
              setShowLabelModal(true);
              return;
            }
            toast({
              title: "Coming Soon",
              description: `${action} feature will be available soon.`,
            });
          }}
        />
      )}

      <AiSmartIngestModal
        isOpen={showSmartIngest}
        onClose={() => setShowSmartIngest(false)}
        onApplyExtractedData={handleApplyExtractedData}
      />

      {/* CREATE LABEL MODAL */}
      <CreateLabelModal
        isOpen={showLabelModal}
        onClose={() => setShowLabelModal(false)}
        docket={docketData || formData}
        locations={locations}
        onPrint={(labelForm) => {
          setShowLabelModal(false);
          const params = new URLSearchParams({
            from: labelForm.fromLocation,
            to: labelForm.toLocation,
            packages: labelForm.packages,
            size: labelForm.printSize,
            additional: labelForm.additionalInfo
          });
          window.open(`/dockets/print-label/${docketId}?${params.toString()}`, '_blank');
        }}
      />

      {/* Bottom action bar */}
      {formData.shipment_type && resolvedSchema && (
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={() => navigate('/dockets')} data-testid="bottom-cancel-btn">
            <X className="w-4 h-4 mr-2" /> Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="bottom-save-btn" className="bg-accent-blue hover:bg-accent-blue/90">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : isEditMode ? 'Update Docket' : 'Create Docket'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CreateDocket;
